import React, {
	Dispatch,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { isBoolean } from "lodash";

import { CarClass } from "../../../../services";
import { useTypedSelector } from "../../../../redux/store";
import MultiSelectWithModalBase, {
	MultiSelectWithModal,
} from "../MultiSelectWithModal";
import { StyledP, StyledRow, StyledColumn } from "../..";

const MultiSelectCarClassWithModalBase: React.FC<
	MultiSelectCarClassWithModalBase.Props
> = ({
	value,
	onChange,
	disabled,
	titleText = "",
	required,
	modalSetting,
	error = "",
	showHeadTitle = false,
	subscribeOptions = {},
	selectOnlyOne,
	...props
}) => {
	const { t } = useTranslation();
	const lang = useTypedSelector((state) => state.session.language);

	const carClasses = useTypedSelector(
		(state) => state.preferencesReducer.carClasses.data.models,
	);

	const [errorInternal, setErrorInternal] = useState<string | boolean>("");
	useEffect(() => {
		setErrorInternal(error);
	}, [error]);

	const items = useMemo(() => {
		if (subscribeOptions?.taxiServiceIds) {
			const { taxiServiceIds } = subscribeOptions;
			return (
				carClasses?.filter((item) =>
					item.taxiServiceIds?.some((taxiServiceId) =>
						taxiServiceIds.includes(taxiServiceId),
					),
				) ?? []
			);
		}

		return carClasses ?? [];
	}, [carClasses, subscribeOptions]);

	const selectOptions = useMemo<MultiSelectWithModalBase.Value>(
		() =>
			items.map((item) => ({
				key: item.id,
				label: item.name?.[lang],
				value: item.id,
				name: item.name?.[lang],
				data: item,
				active: isBoolean(item.active) ? item.active : undefined,
			})),
		[items, lang],
	);

	const selected = useMemo<MultiSelectWithModalBase.Value>(
		() =>
			selectOptions.filter((item) =>
				value.find((data) => data === item.value),
			),

		[selectOptions, value],
	);

	const selectOnChange = useCallback(
		(newValue: MultiSelectWithModalBase.Value) => {
			setErrorInternal("");
			const Ids = newValue.map((item) => item.value);
			onChange(Ids);
		},
		[onChange],
	);

	const texts = useMemo(
		() => ({
			title: t(titleText),
			allText: t(["all_cars"]),
			allActiveText: t(["all_active_cars"]),
		}),
		[t, titleText],
	);

	const valuesOptions = useMemo(() => {
		const arr = Array.isArray(selectOptions) ? selectOptions : [];
		const options = [...arr].filter((item) => {
			if (!isBoolean(item.active)) return true;
			return item.active === true;
		});

		const actives = [...arr].filter(
			(item) => isBoolean(item.active) && item.active === true,
		);

		const notActives = [...arr].filter(
			(item) => isBoolean(item.active) && item.active === false,
		);

		return {
			optionsLength: options.length,
			valueLength: selected.length,
			actives,
			notActives,
		};
	}, [selectOptions, selected]);

	return (
		<StyledRow gap="7px" alignItems="center">
			<StyledColumn gap="7px" flex={{ flex: "1" }}>
				{showHeadTitle && <StyledP>{t(titleText) ?? ""}</StyledP>}
				<MultiSelectWithModal
					error={disabled ? false : errorInternal}
					disabled={disabled}
					value={selected}
					options={selectOptions}
					actives={valuesOptions.actives}
					notActives={valuesOptions.notActives}
					optionsLength={valuesOptions.optionsLength}
					valueLength={valuesOptions.valueLength}
					title={texts.title}
					allText={selectOnlyOne ? texts.title : texts.allText}
					allActiveText={
						selectOnlyOne ? texts.title : texts.allActiveText
					}
					onChange={selectOnChange}
					required={required}
					pullUpItemInsideArray
					hiddenButton={false}
					showSelectAll
					modalSetting={modalSetting}
					sort={{
						show: true,
						active: true,
						// ...(sort || {}),
					}}
					selectOnlyOne={selectOnlyOne}
					{...props}
				/>
			</StyledColumn>
		</StyledRow>
	);
};

export const MultiSelectCarClassWithModal = memo(
	MultiSelectCarClassWithModalBase,
);

declare namespace MultiSelectCarClassWithModalBase {
	interface Props extends MultiSelectWithModalBase.Setting {
		value: Value;
		onChange: Dispatch<Value>;
		titleText?: string | string[];
		modalSetting?: MultiSelectWithModalBase.ListSelectProps["modalSetting"];
		error?: string | boolean;
		subscribeOptions?: CarClass.SubscribeOptions;

		showHeadTitle?: boolean;
	}

	type Value = number[];
}

export default MultiSelectCarClassWithModalBase;
